import { Suspense, useRef, useMemo, useEffect } from "react";
import { Canvas, useFrame, useLoader, useThree } from "@react-three/fiber";
import { OrbitControls, Stars } from "@react-three/drei";
import * as THREE from "three";
import { TextureLoader } from "three";
import { pointToSector, latLngToVector3 } from "@/lib/sectors";

function MarsBody({ meshRef, interactive, marker, positionX = 0 }) {
  const [colorMap, bumpMap] = useLoader(TextureLoader, [
    "/textures/mars.jpg",
    "/textures/mars_bump.jpg",
  ]);

  useMemo(() => {
    [colorMap, bumpMap].forEach((t) => {
      if (t) t.colorSpace = THREE.SRGBColorSpace;
    });
  }, [colorMap, bumpMap]);

  useFrame((_, delta) => {
    if (meshRef.current && !interactive) {
      meshRef.current.rotation.y += delta * 0.04;
    }
  });

  return (
    <group>
      <mesh ref={meshRef} position={[positionX, 0, 0]}>
        <sphereGeometry args={[2, 96, 96]} />
        <meshStandardMaterial
          map={colorMap}
          bumpMap={bumpMap}
          bumpScale={0.04}
          metalness={0.1}
          roughness={0.9}
        />
        {marker && <MarkerOnGlobe lat={marker.lat} lng={marker.lng} />}
      </mesh>
      <mesh scale={1.06} position={[positionX, 0, 0]}>
        <sphereGeometry args={[2, 64, 64]} />
        <meshBasicMaterial color="#00D4FF" transparent opacity={0.06} side={THREE.BackSide} />
      </mesh>
    </group>
  );
}

function MarkerOnGlobe({ lat, lng }) {
  const pos = latLngToVector3(lat, lng, 2.02);
  return (
    <group position={pos}>
      <mesh>
        <sphereGeometry args={[0.025, 16, 16]} />
        <meshBasicMaterial color="#00D4FF" />
      </mesh>
      <pointLight color="#00D4FF" intensity={2} distance={0.6} />
    </group>
  );
}

// Manual raycasting via DOM listeners — robust across R3F event-system quirks.
function Picker({ meshRef, onPick, onHover }) {
  const { camera, gl, raycaster } = useThree();
  useEffect(() => {
    const el = gl.domElement;
    const ndc = new THREE.Vector2();
    let down = null;

    const hitAt = (clientX, clientY) => {
      const rect = el.getBoundingClientRect();
      ndc.x = ((clientX - rect.left) / rect.width) * 2 - 1;
      ndc.y = -((clientY - rect.top) / rect.height) * 2 + 1;
      raycaster.setFromCamera(ndc, camera);
      if (!meshRef.current) return null;
      const hits = raycaster.intersectObject(meshRef.current, false);
      return hits.length ? hits[0] : null;
    };

    const onMove = (e) => {
      if (!onHover || e.target !== el) { if (onHover) onHover(null); return; }
      const h = hitAt(e.clientX, e.clientY);
      if (h && h.point) {
        const s = pointToSector(h.point.x, h.point.y, h.point.z);
        onHover(s, { x: e.clientX, y: e.clientY });
      } else {
        onHover(null);
      }
    };

    const onDown = (e) => { down = e.target === el ? { x: e.clientX, y: e.clientY } : null; };
    const onUp = (e) => {
      if (!onPick || !down || e.target !== el) { down = null; return; }
      const dist = Math.hypot(e.clientX - down.x, e.clientY - down.y);
      down = null;
      if (dist > 6) return;
      const h = hitAt(e.clientX, e.clientY);
      if (h && h.point) {
        const s = pointToSector(h.point.x, h.point.y, h.point.z);
        onPick(s, [h.point.x, h.point.y, h.point.z]);
      }
    };

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerdown", onDown);
    window.addEventListener("pointerup", onUp);
    el.style.cursor = "crosshair";
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerdown", onDown);
      window.removeEventListener("pointerup", onUp);
    };
  }, [camera, gl, raycaster, meshRef, onPick, onHover]);
  return null;
}

export function MarsGlobe({ onPick, onHover, interactive = true, marker, enableZoom = true, positionX = 0 }) {
  const meshRef = useRef();
  return (
    <Canvas
      camera={{ position: [0, 0, 6], fov: 42 }}
      gl={{ antialias: true, alpha: true }}
      dpr={[1, 2]}
    >
      <ambientLight intensity={0.35} />
      <directionalLight position={[5, 3, 5]} intensity={2.2} color="#fff5ee" />
      <directionalLight position={[-5, -2, -3]} intensity={0.4} color="#00D4FF" />
      <Suspense fallback={null}>
        <Stars radius={120} depth={60} count={4000} factor={4} saturation={0} fade speed={0.5} />
        <MarsBody meshRef={meshRef} interactive={interactive} marker={marker} positionX={positionX} />
      </Suspense>
      {interactive && <Picker meshRef={meshRef} onPick={onPick} onHover={onHover} />}
      <OrbitControls
        enablePan={false}
        enableZoom={enableZoom}
        minDistance={3.2}
        maxDistance={9}
        autoRotate={false}
        rotateSpeed={0.5}
        zoomSpeed={0.7}
      />
    </Canvas>
  );
}
